import React from "react";
import { useEffect, useState, useRef, useMemo } from "react";
import EventDialog from "./EventDialog";
import NewEventDialog from "./NewEventDialog";
import CalendarHeader from "./CalendarHeader";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon";
import esLocale from "@fullcalendar/core/locales/es";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  deleteField,
} from "firebase/firestore";
import { db } from "../../firebase";
import { calendarStyles } from "./styles";
import { formatEventDate } from "./utils";
import { spanishMonths, getYears, getDaysInMonth } from "./utils";
import { showAlert } from "../../alertService";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import YearView from "./YearView";
import MonthSpacesView from "./MonthSpacesView";
import { useNavigate, useSearchParams } from "react-router-dom";
import DialogoConfirmacion from "../shared/DialogoConfirmacion";
import { TrashIcon as TrashOutlineIcon } from "@heroicons/react/24/outline";
import { TIPOS_BORRABLES } from "./utils";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";

import { esSoloLectura } from "../../utils/roles";
import {
  footprintDeEvento,
  footprintDesdeResumen,
  eventoBloqueaEspacio,
} from "../../utils/espacios";

// Colores de acento por espacio (borde del evento), distintos de los colores
// por eventType que pintan el fondo. Se asignan por posición en el rollup.
const ESPACIO_COLORS = [
  "#0EA5E9",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#EAB308",
  "#6366F1",
  "#84CC16",
  "#F97316",
];

// La vista de carriles no monta FullCalendar, pero EventDialog y
// handleSaveEvent hablan la API de su objeto de evento (setProp /
// setExtendedProp). Este adaptador expone la misma superficie; los setters
// son solo compensación de latencia — el onSnapshot del calendario refresca
// igual.
const makeEventHandle = (ev) => ({
  id: ev.id,
  title: ev.title,
  start: ev.start,
  end: ev.end,
  extendedProps: { ...ev.extendedProps },
  setProp(k, v) {
    this[k] = v;
  },
  setExtendedProp(k, v) {
    this.extendedProps[k] = v;
  },
});

export default function Calendario({ isSidebarCollapsed }) {
  const calendarRef = useRef(null);
  const containerRef = useRef(null);
  const pendingDateRef = useRef(null);
  const {
    selectedVenueId,
    events: rawEvents,
    calendarTimezone,
    espaciosResumen,
    espaciosActivos,
    espaciosPorId,
    permiteEventosSimultaneos,
  } = useVenue();
  const { userInfo, user } = useAuth();
  const isViewer = userInfo?.role === "viewer";
  // Visor e inversionista no crean ni editan eventos (P-FRA-13); isViewer
  // solo decide qué datos se ocultan (precio acordado, notas).
  const soloLectura = esSoloLectura(userInfo);

  const timeZone = calendarTimezone;

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editName, setEditName] = useState("");
  const [editEspacios, setEditEspacios] = useState([]);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [viewType, setViewType] = useState("all");
  // Qué espacios se están mirando. null = todos (estado inicial y el que deja
  // el cambio de venue). En la vista de carriles decide qué carriles se
  // pintan; en semana/año filtra los eventos.
  const [espaciosVisibles, setEspaciosVisibles] = useState(null);
  const [calendarView, setCalendarView] = useState("dayGridMonth");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isNewEventDialogOpen, setIsNewEventDialogOpen] = useState(false);
  const [newEventData, setNewEventData] = useState({});
  const [showYearView, setShowYearView] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Por borrar (scouting, reunión o bloqueo): { id, nombre, eventType }.
  const [porBorrar, setPorBorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);

  // Navigate to pending date after switching from year view to month view
  useEffect(() => {
    if (!showYearView && pendingDateRef.current && calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.gotoDate(pendingDateRef.current);
      pendingDateRef.current = null;
    }
  }, [showYearView]);

  // El rollup de espacios vive en VenueProvider (suscripción viva al doc del
  // venue): calendario, leads y ocupación leen exactamente lo mismo. Venue sin
  // espacios ⇒ resumen vacío ⇒ el calendario se comporta como antes de
  // multiespacio.
  const espacioColor = useMemo(() => {
    const map = {};
    espaciosResumen.forEach((e, i) => {
      map[e.id] = ESPACIO_COLORS[i % ESPACIO_COLORS.length];
    });
    return map;
  }, [espaciosResumen]);

  // Al cambiar de venue, los espacios filtrados no existen en el nuevo
  useEffect(() => {
    setEspaciosVisibles(null);
  }, [selectedVenueId]);

  // Carriles: un venue con 2+ espacios reservables no se puede leer en el
  // grid de mes clásico (mezcla todos los salones en una sola pila). Con 0 o
  // 1 espacio la vista de siempre es la correcta y no se toca.
  const laneMode =
    espaciosActivos.length >= 2 && !showYearView && calendarView === "dayGridMonth";

  // Mes/año visibles leídos en la zona del venue (ver CalendarHeader).
  const { laneYear, laneMonth } = useMemo(() => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || "America/Mexico_City",
      month: "numeric",
      year: "numeric",
    }).formatToParts(currentDate);
    return {
      laneYear: Number(parts.find((p) => p.type === "year").value),
      laneMonth: Number(parts.find((p) => p.type === "month").value) - 1,
    };
  }, [currentDate, timeZone]);

  const espaciosCarril = useMemo(
    () =>
      espaciosVisibles
        ? espaciosActivos.filter((e) => espaciosVisibles.includes(e.id))
        : espaciosActivos,
    [espaciosActivos, espaciosVisibles]
  );

  // Transform raw events from context into FullCalendar format
  const events = useMemo(() => {
    return rawEvents.map((event) => {
      const { id, startTime, endTime, eventType, nombre, status, espaciosIds } = event;
      const isCancelled = status === "cancelado";
      const primerEspacio = (espaciosIds || [])[0];
      return {
        id,
        title: isCancelled ? `[Cancelado] ${nombre || "Sin nombre"}` : (nombre || "Sin nombre"),
        start: startTime && startTime.toDate ? startTime.toDate() : startTime,
        end: endTime && endTime.toDate ? endTime.toDate() : endTime,
        color: isCancelled
          ? "#9CA3AF"
          : eventType === "scouting"
            ? "#FF9900"
            : eventType === "reunion"
            ? "#3B82F6"
            : eventType === "bloqueo"
            ? "#DC2626"
            : "#1b4d2e",
        borderColor:
          !isCancelled && primerEspacio && espacioColor[primerEspacio]
            ? espacioColor[primerEspacio]
            : undefined,
        extendedProps: {
          eventType,
          ...event,
        },
      };
    });
  }, [rawEvents, espacioColor]);

  // Handle window resize and sidebar state changes with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (calendarRef.current) {
          const width = entry.contentRect.width;
          const minCellWidth = 100;
          const numDays = 7;
          const availableWidth = width - 40;
          const cellWidth = Math.max(
            minCellWidth,
            Math.floor(availableWidth / numDays)
          );

          const calendar = calendarRef.current.getApi();
          calendar.setOption("dayCellClassNames", `min-w-[${cellWidth}px]`);
          calendar.render();
        }
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [isSidebarCollapsed]);

  // Filter events based on view type
  const filteredEvents = events.filter((event) => {
    const matchesType = (() => {
      if (viewType === "all") return true;
      if (viewType === "scouting")
        return event.extendedProps.eventType === "scouting";
      if (viewType === "reunion")
        return event.extendedProps.eventType === "reunion";
      if (viewType === "bloqueo")
        return event.extendedProps.eventType === "bloqueo";
      if (viewType === "evento") return event.extendedProps.eventType === "event";
      return event.extendedProps.eventType === viewType;
    })();
    if (!matchesType) return false;
    // Filtro por espacio: muestra lo que BLOQUEA alguno de los espacios
    // visibles (contención, incompatibilidad, o eventos legacy que ocupan
    // todo el venue). En carriles el propio carril ya hace este trabajo.
    if (!espaciosVisibles || espaciosVisibles.length === 0) return true;
    const fp = footprintDeEvento(event);
    return espaciosVisibles.some((id) => {
      const entry = espaciosPorId[id];
      return entry ? eventoBloqueaEspacio(fp, entry) : false;
    });
  });

  const eventsForYear = events.filter(e => {
    // Use startTime or start, and endTime or end
    let start = e.startTime || e.start;
    let end = e.endTime || e.end;
  
    // Convert Firestore Timestamp to JS Date
    if (start && start.toDate) start = start.toDate();
    else if (typeof start === "string" || typeof start === "number") start = new Date(start);
  
    if (end && end.toDate) end = end.toDate();
    else if (typeof end === "string" || typeof end === "number") end = new Date(end);
  
    if (!start || isNaN(start)) return false;
    if (!end || isNaN(end)) end = start;
  
    // Check if event overlaps the selected year
    const yearStart = new Date(selectedYear, 0, 1, 0, 0, 0, 0);
    const yearEnd = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
    return end >= yearStart && start <= yearEnd;
  });

  // Event click: open dialog for event details
  const handleEventClick = (clickInfo) => {
    setSelectedEvent(clickInfo.event);
    setEditName(clickInfo.event.extendedProps.nombre || "");
    setEditNotes(clickInfo.event.extendedProps.notes || "");
    setEditEspacios(clickInfo.event.extendedProps.espaciosIds || []);
    setIsEventDialogOpen(true);
    clickInfo.el.blur();
  };

  // Save name and notes update for an existing event
  const handleSaveEvent = async () => {
    if (!selectedVenueId || !selectedEvent) return;
    try {
      const eventDocRef = doc(
        db,
        "venues",
        selectedVenueId,
        "calendars",
        "default",
        "events",
        selectedEvent.id
      );
      const updates = { nombre: editName, notes: editNotes };
      // Espacios: el footprint se re-resuelve al guardar con el rollup
      // vigente. Deseleccionar todo vuelve el evento a "todo el venue"
      // (se eliminan los campos, como un evento legacy). En venues sin
      // espacios no se tocan estos campos.
      if (espaciosResumen.length > 0) {
        const original = selectedEvent.extendedProps.espaciosIds || [];
        const cambiaron =
          JSON.stringify([...original].sort()) !==
          JSON.stringify([...editEspacios].sort());
        if (cambiaron) {
          if (editEspacios.length > 0) {
            const footprint = footprintDesdeResumen(editEspacios, espaciosPorId);
            updates.espaciosIds = editEspacios;
            updates.unidadesOcupadas = footprint ? [...footprint].sort() : [];
          } else {
            updates.espaciosIds = deleteField();
            updates.unidadesOcupadas = deleteField();
          }
        }
      }
      await updateDoc(eventDocRef, updates);
      selectedEvent.setProp("title", editName || "Sin nombre");
      selectedEvent.setExtendedProp("nombre", editName);
      selectedEvent.setExtendedProp("notes", editNotes);
      if (espaciosResumen.length > 0) {
        selectedEvent.setExtendedProp(
          "espaciosIds",
          editEspacios.length > 0 ? editEspacios : undefined
        );
        selectedEvent.setExtendedProp(
          "unidadesOcupadas",
          editEspacios.length > 0
            ? [...(footprintDesdeResumen(editEspacios, espaciosPorId) || [])].sort()
            : undefined
        );
      }
      setIsEventDialogOpen(false);
      showAlert("Evento actualizado", "success");
      logBitacora(selectedVenueId, {
        action: BITACORA_ACTIONS.EVENT_EDIT,
        entity: "event",
        entityId: selectedEvent.id,
        entityName: editName || "",
        details: {},
      }, userInfo, user);
    } catch (error) {
      console.error("Error updating event:", error);
      showAlert("Error al actualizar el evento", "error");
    }
  };


  // Borrar del calendario: solo scouting, reuniones y bloqueos. Se cierra
  // primero el detalle para que la confirmación no quede detrás de él.
  const handleDeleteEvent = () => {
    const tipo = selectedEvent?.extendedProps?.eventType;
    if (!selectedVenueId || !selectedEvent || !TIPOS_BORRABLES.includes(tipo)) return;
    setPorBorrar({
      id: selectedEvent.id,
      nombre: selectedEvent.extendedProps?.nombre || selectedEvent.title || "",
      eventType: tipo,
    });
    setIsEventDialogOpen(false);
  };
  const confirmarBorrado = async () => {
    if (!porBorrar || !TIPOS_BORRABLES.includes(porBorrar.eventType)) return;
    setBorrando(true);
    try {
      await deleteDoc(doc(db, "venues", selectedVenueId, "calendars", "default", "events", porBorrar.id));
      logBitacora(selectedVenueId, {
        action: BITACORA_ACTIONS.EVENT_DELETE,
        entity: "event",
        entityId: porBorrar.id,
        entityName: porBorrar.nombre,
        details: { eventType: porBorrar.eventType },
      }, userInfo, user);
      showAlert("Eliminado del calendario", "success");
      setPorBorrar(null);
    } catch (error) {
      console.error("Error deleting event:", error);
      showAlert(`No se pudo eliminar. [${error.message}]`, "error");
    } finally {
      setBorrando(false);
    }
  };

  // «Nuevo evento» desde la lista de eventos llega aquí con ?nuevo=1: se
  // abre el mismo formulario del calendario (con su revisión de choques) y
  // se limpia el parámetro para que recargar no lo vuelva a abrir.
  useEffect(() => {
    if (searchParams.get("nuevo") !== "1") return;
    if (selectedVenueId && !soloLectura) {
      setNewEventData({ timezone: timeZone });
      setIsNewEventDialogOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("nuevo");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, selectedVenueId, soloLectura, timeZone]);

  // Save new event including client data
  const handleSaveNewEvent = async () => {
    if (!selectedVenueId) return;
    try {
      const {
        startTime,
        endTime,
        eventType,
        inicioMontaje,
        inicioEvento,
        finEvento,
        finMontaje,
        clientType,
        espaciosIds,
        ...otherFields
      } = newEventData;

      const eventData = {
        startTime: new Date(startTime),
        endTime: new Date(endTime),
        eventType,
        clientType: clientType || 'fisica',
        ...otherFields,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Footprint congelado al guardar. Solo se escriben los campos si el
      // venue tiene espacios Y el owner seleccionó alguno — un evento sin
      // selección (o en un venue sin espacios) queda como hoy: sin campos,
      // que todo consumidor lee como "todo el venue".
      if (espaciosResumen.length > 0 && espaciosIds && espaciosIds.length > 0) {
        const footprint = footprintDesdeResumen(espaciosIds, espaciosPorId);
        eventData.espaciosIds = espaciosIds;
        eventData.unidadesOcupadas = footprint ? [...footprint].sort() : [];
      }

      // Un evento nace SIEMPRE con cliente del directorio (el diálogo lo
      // exige). Aquí ya no se fabrican fichas con lo tecleado: eso era lo
      // que llenaba el directorio de clientes huérfanos y duplicados.
      if (eventType === "event" && !eventData.clienteId) {
        showAlert("Elige el cliente del evento", "error");
        return;
      }
      if (!eventData.clienteId) delete eventData.clienteId;

      // Add optional date fields if they exist
      if (inicioMontaje) eventData.inicioMontaje = new Date(inicioMontaje);
      if (inicioEvento) eventData.inicioEvento = new Date(inicioEvento);
      if (finEvento) eventData.finEvento = new Date(finEvento);
      if (finMontaje) eventData.finMontaje = new Date(finMontaje);
      delete eventData.timeZone;
      delete eventData.clickInfo;

      // First, check if calendar exists and create it if it doesn't
      const calendarDocRef = doc(
        db,
        "venues",
        selectedVenueId,
        "calendars",
        "default"
      );
      const calendarSnapshot = await getDoc(calendarDocRef);

      if (!calendarSnapshot.exists()) {
        // Create calendar document with default timezone
        await setDoc(calendarDocRef, {
          timezone: "America/Mexico_City",
          createdAt: serverTimestamp(),
        });
      }

      // Now add the event
      const docRef = await addDoc(
        collection(
          db,
          "venues",
          selectedVenueId,
          "calendars",
          "default",
          "events"
        ),
        eventData
      );
      setIsNewEventDialogOpen(false);
      showAlert("Evento creado exitosamente", "success");
      logBitacora(selectedVenueId, {
        action: BITACORA_ACTIONS.EVENT_CREATE,
        entity: "event",
        entityId: docRef.id,
        entityName: otherFields.nombre || "",
        details: { eventType },
      }, userInfo, user);

      // Only redirect if eventType is 'event'
      if (eventType === "event") {
        navigate(`/dashboard/eventos/${docRef.id}`);
      }
    } catch (error) {
      console.error("Error adding new event:", error);
      showAlert("Error al crear el evento", "error");
    }
  };

  // Handle date click to open new event dialog (disabled for solo lectura)
  const handleDateClick = (clickInfo) => {
    if (soloLectura) return;
    setNewEventData({
      timeZone: timeZone,
      clickInfo: clickInfo,
    });
    setIsNewEventDialogOpen(true);
  };

  return (
    <div className="relative flex flex-col items-center space-y-4">
      <style>{calendarStyles}</style>

      {/* Calendar */}
      {selectedVenueId && (
        <>
          <CalendarHeader
            calendarView={calendarView}
            setCalendarView={setCalendarView}
            viewType={viewType}
            setViewType={setViewType}
            espacios={espaciosActivos}
            espaciosVisibles={espaciosVisibles}
            setEspaciosVisibles={setEspaciosVisibles}
            espacioColor={espacioColor}
            laneMode={laneMode}
            calendarRef={calendarRef}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            timeZone={timeZone}
            showYearView={showYearView}
            setShowYearView={setShowYearView}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            soloLectura={soloLectura}
            onNewEvent={selectedVenueId ? () => {
              setNewEventData({ timezone: timeZone });
              setIsNewEventDialogOpen(true);
            } : undefined}
          />
          <div ref={containerRef} className="w-full">
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
              {laneMode ? (
                <MonthSpacesView
                  year={laneYear}
                  month={laneMonth}
                  espacios={espaciosCarril}
                  events={filteredEvents}
                  espacioColor={espacioColor}
                  timeZone={timeZone}
                  soloLectura={soloLectura}
                  onEventClick={(ev) => {
                    // El diálogo espera la API de un evento de FullCalendar
                    // (setProp/setExtendedProp al guardar). Se le pasa un
                    // adaptador con la misma superficie.
                    setSelectedEvent(makeEventHandle(ev));
                    setEditName(ev.extendedProps.nombre || "");
                    setEditNotes(ev.extendedProps.notes || "");
                    setEditEspacios(ev.extendedProps.espaciosIds || []);
                    setIsEventDialogOpen(true);
                  }}
                  onDateClick={(iso) => {
                    setNewEventData({
                      timeZone,
                      clickInfo: { dateStr: iso, date: new Date(`${iso}T00:00:00`) },
                    });
                    setIsNewEventDialogOpen(true);
                  }}
                />
              ) : showYearView ? (
                <YearView
                  year={selectedYear}
                  events={eventsForYear}
                  espacios={espaciosCarril}
                  espacioColor={espacioColor}
                  timeZone={timeZone}
                  onEventClick={(ev) => {
                    setSelectedEvent(makeEventHandle(ev));
                    setEditName(ev.extendedProps.nombre || "");
                    setEditNotes(ev.extendedProps.notes || "");
                    setEditEspacios(ev.extendedProps.espaciosIds || []);
                    setIsEventDialogOpen(true);
                  }}
                  onMonthClick={(year, month) => {
                    pendingDateRef.current = new Date(year, month, 1);
                    setCalendarView("dayGridMonth");
                    setShowYearView(false);
                  }}
                />
              ) : (
                <FullCalendar
                  ref={calendarRef}
                  plugins={[
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin,
                    luxonPlugin,
                  ]}
                  initialView={calendarView}
                  initialDate={currentDate}
                  events={filteredEvents}
                  eventClick={handleEventClick}
                  dateClick={handleDateClick}
                  height="auto"
                  timeZone={timeZone}
                  locale={esLocale}
                  eventClassNames={() => "cursor-pointer"}
                  headerToolbar={false}
                  allDaySlot={false}
                  expandRows={true}
                  stickyHeaderDates={true}
                  dayMaxEvents={true}
                  slotMinTime="00:00:00"
                  slotMaxTime="23:59:59"
                  slotDuration="00:30:00"
                  datesSet={(dateInfo) => {
                    setCalendarView(dateInfo.view.type);
                    setCurrentDate(dateInfo.view.currentStart);
                  }}
                  slotLabelFormat={{
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  }}
                  eventTimeFormat={{
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  }}
                  eventDisplay="block"
                  eventBackgroundColor="#1b4d2e"
                  eventBorderColor="transparent"
                  eventTextColor="#ffffff"
                  eventColor="#1b4d2e"
                  views={{
                    dayGridMonth: {
                      titleFormat: { year: "numeric", month: "long" },
                      dayMaxEvents: true,
                      moreLinkText: (count) => `+${count} más`,
                    },
                    timeGridWeek: {
                      titleFormat: {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                      slotDuration: "00:30:00",
                      slotMinTime: "00:00:00",
                      slotMaxTime: "23:59:59",
                      slotLabelFormat: {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      },
                    },
                  }}
                  windowResize={false}
                  handleWindowResize={false}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Event Details Dialog */}
      <EventDialog
        isOpen={isEventDialogOpen}
        onClose={() => setIsEventDialogOpen(false)}
        selectedEvent={selectedEvent}
        editName={editName}
        setEditName={setEditName}
        editNotes={editNotes}
        setEditNotes={setEditNotes}
        handleSaveEvent={handleSaveEvent}
        handleDeleteEvent={handleDeleteEvent}
        timeZone={timeZone}
        formatEventDate={formatEventDate}
        isViewer={isViewer}
        soloLectura={soloLectura}
        espaciosActivos={espaciosActivos}
        espaciosPorId={espaciosPorId}
        editEspacios={editEspacios}
        setEditEspacios={setEditEspacios}
        existingEvents={events.filter(e => e.extendedProps?.status !== "cancelado")}
        permiteEventosSimultaneos={permiteEventosSimultaneos}
      />

      {/* New Event Dialog */}
      <NewEventDialog
        isOpen={isNewEventDialogOpen}
        onClose={() => setIsNewEventDialogOpen(false)}
        newEventData={newEventData}
        setNewEventData={setNewEventData}
        handleSaveNewEvent={handleSaveNewEvent}
        spanishMonths={spanishMonths}
        getYears={getYears}
        getDaysInMonth={getDaysInMonth}
        timeZone={timeZone}
        existingEvents={events.filter(e => e.extendedProps?.status !== "cancelado")}
        espaciosActivos={espaciosActivos}
        espaciosPorId={espaciosPorId}
        permiteEventosSimultaneos={permiteEventosSimultaneos}
      />

      <DialogoConfirmacion
        open={!!porBorrar && !soloLectura}
        onClose={() => setPorBorrar(null)}
        saving={borrando}
        icono={TrashOutlineIcon}
        titulo={
          porBorrar?.eventType === "bloqueo"
            ? "Eliminar bloqueo"
            : porBorrar?.eventType === "reunion"
            ? "Eliminar reunión"
            : "Eliminar scouting"
        }
        subtitulo={porBorrar?.nombre || ""}
        confirmar={{ label: "Eliminar", savingLabel: "Eliminando…", tono: "rojo", onClick: confirmarBorrado }}
      >
        <p>Desaparece del calendario y libera esas fechas. Esta acción no se puede deshacer.</p>
      </DialogoConfirmacion>
    </div>
  );
}
